<template>
  <video
    ref="element"
    class="def-video"
    v-bind="videoAttrs"
    :muted="muted"
    :loop="loop"
    :controls="controls"
    :autoplay="priority"
    :preload="priority || loadVideo ? 'auto' : 'none'"
    playsinline
    @error="onVideoError"
  ></video>
</template>

<script>
import useDeviceInfo from '@use/useDeviceInfo';

export default {
  name: 'DefVideo',

  props: {
    result: {
      type: Object,
    },
    muted: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    controls: {
      type: Boolean,
      default: false,
    },
    priority: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loadObserver: null,
      playObserver: null,

      loadVideo: false,
      isVisible: false,

      deviceScreen: useDeviceInfo().screen,
    };
  },

  computed: {
    getVideo() {
      return this.result?.[this.deviceScreen] ?? this.result;
    },

    videoAttrs() {
      if (this.priority || this.loadVideo) {
        return this.getVideo;
      }

      return {
        width: this.getVideo?.width,
        height: this.getVideo?.height,
      };
    },

    width() {
      return this.getVideo?.width;
    },

    height() {
      return this.getVideo?.height;
    },
  },

  mounted() {
    try {
      const video = this.$refs.element;

      if (!this.result || !video) {
        return;
      }

      this.validateVideo();

      // Відео першого екрана:
      // src є одразу + autoplay.
      if (this.priority) {
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.loadVideo = true;
        return;
      }

      // 1. Завантажуємо відео трохи ДО viewport
      this.loadObserver = new IntersectionObserver(this.loadObserverCallBack, {
        rootMargin: '200px 0px',
        threshold: 0.01,
      });

      // 2. Play / pause тільки коли відео реально у viewport
      this.playObserver = new IntersectionObserver(this.playObserverCallBack, {
        rootMargin: '0px',
        threshold: 0.5,
      });

      this.loadObserver.observe(video);
      this.playObserver.observe(video);
    } catch (err) {
      console.error(err);
    }
  },

  beforeUnmount() {
    try {
      this.loadObserver?.disconnect();
      this.playObserver?.disconnect();
    } catch (err) {
      console.error(err);
    }
  },

  methods: {
    loadObserverCallBack(entries) {
      const entry = entries[0];

      if (!entry.isIntersecting || this.loadVideo) {
        return;
      }

      this.loadVideo = true;

      // дублює завантаження відео в нетворкі.
      // this.$nextTick(() => {
      //   this.$refs.element?.load();
      // });

      // Завантажуємо тільки один раз.
      this.loadObserver?.unobserve(entry.target);

      // playObserver міг спрацювати раніше (порядок колбеків не гарантований) —
      // запускаємо тут, коли src вже потрапить у DOM.
      this.$nextTick(() => {
        if (this.isVisible) this.playVideo();
      });
    },

    playObserverCallBack(entries) {
      const entry = entries[0];

      this.isVisible = entry.isIntersecting;

      if (!this.loadVideo) {
        return;
      }

      if (!entry.isIntersecting) {
        this.$refs.element?.pause();
        return;
      }

      this.playVideo();
    },

    playVideo() {
      this.$refs.element?.play().catch((err) => {
        // autoplay-політики та pause під час play — штатна ситуація
        if (['NotAllowedError', 'AbortError'].includes(err?.name)) return;
        console.error('[DefVideo] play failed', err);
      });
    },

    /**
     * перевіряє що в даних саме відео, а не картинка
     */
    validateVideo() {
      const { src, type } = this.getVideo || {};

      if (!src) {
        console.warn('[DefVideo] invalid video: "src" is empty, got', this.getVideo);
        return;
      }

      const isImageType = type && !type.startsWith('video/');
      const isImageSrc = /\.(jpe?g|png|gif|webp|avif|svg|bmp)(\?.*)?$/i.test(src);

      if (isImageType || isImageSrc) {
        console.warn('[DefVideo] invalid video: looks like an image, got', this.getVideo);
      }
    },

    onVideoError() {
      const { error, currentSrc } = this.$refs.element || {};
      const reasons = {
        1: 'aborted',
        2: 'network error',
        3: 'decode error',
        4: 'source not supported (wrong format or 404)',
      };

      console.error(
        `[DefVideo] failed to load video: ${reasons[error?.code] || 'unknown error'}`,
        currentSrc || this.getVideo?.src
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.def-video {
  display: block;
  max-width: 100%;
  height: auto;
  aspect-ratio: auto v-bind(width) / v-bind(height);
  object-fit: cover;
  width: 100%;
}
</style>
